/**
 * Base application class of the web app (WebApp extends it)
 * Provides common initialization, language management, Gateway integration, and UI utilities
 */

import Modal from 'bootstrap/js/src/modal.js';
import Offcanvas from 'bootstrap/js/src/offcanvas.js';
import DocumentParser from './document-parser';
import DocumentHistory from './document-history';
import FileHandleManager from './file-handle-manager';
import LanguageSelector from '../components/language-selector';
import StatusBar from '../components/status-bar';
import RecentDocuments from '../components/recent-documents';
import AccountSettings from '../components/account-settings';
import DiscoveryPanel from '../components/discovery-panel';
import ContradictionOffcanvas from '../components/contradiction-offcanvas';
import UIManager from '../i18n/ui-manager';
import UILanguageButton from '../i18n/ui-language-button';
import GatewayService from './gateway-service';
import type { CheckRulesResponse } from './gateway-service';
import Typo3AuthService from './typo3-auth-service';
import SignedOutDialog from '../components/signed-out-dialog';
import {
  setSessionEndedListener,
  notifySessionEnded,
  hasSessionEnded,
  type SessionEndInfo,
} from './session-ended';
import UserManagementService from './user-management-service';
import DocumentManager from './document-manager';
import type { Document } from './document-manager';
import ComponentInitializer from './component-initializer';
import { STORAGE_KEYS } from './app-constants';
import { toIso6393, type LanguageCode } from './language-constants';
import { GatewayInitializer } from './gateway-initializer';
import { logger } from './logger';
import { clearPersonData } from './person-data';
import { forbiddenMessage } from './forbidden-message';
import { broadcastAuthEvent, listenForAuthEvents } from './cross-tab-auth';
import {
  clearLoginRedirects,
  isLoginLooping,
  recordLoginRedirect,
} from './login-loop-guard';
import { GatewayErrorCode, isGatewayError } from './gateway/error-codes';
import { interactionLabels } from './relations/interaction-labels';
import {
  loadSavedDocument,
  saveDocumentForRestore,
  type DocumentRestoreMetadata,
} from './utilities/document-restore';
import {
  highlightAllStatements,
  clearRuleHighlights,
  attachRuleClickListener,
} from './utilities/document-rule-locator';
import { useUserStore } from '../stores/user-store';
import { useAppStore } from '../stores/app-store';
import ToastService from '../components/toast-service';
import SplashScreen from './splash-screen';
import { recordProcessedDocument } from './processed-documents';

class BaseApp {
  // Core services
  protected parser: DocumentParser;
  protected currentLanguage: LanguageCode;
  protected uiManager: UIManager;
  protected authService: Typo3AuthService | null;
  protected userManagementService: UserManagementService | null;
  protected gatewayService: GatewayService;
  protected documentHistory: DocumentHistory;
  protected fileHandleManager: FileHandleManager;

  // Managers
  protected documentManager: DocumentManager | null;
  protected componentInitializer: ComponentInitializer | null;

  // Services
  protected toastService: ToastService;
  protected splashScreen: SplashScreen;

  // UI Components
  protected languageSelector: LanguageSelector | null;
  protected statusBar: StatusBar | null;
  protected uiLanguageButton: UILanguageButton | null;
  protected recentDocuments: RecentDocuments | null;
  protected accountSettings: AccountSettings | null;
  protected discoveryPanel: DiscoveryPanel | null;
  protected contradictionOffcanvas: ContradictionOffcanvas | null;
  /** Detach fn for the document → offcanvas click listener (#72). */
  private detachRuleClickListener: (() => void) | null = null;

  // State
  protected elements: Record<string, HTMLElement | undefined>;

  // Optional properties that subclasses may set
  protected fileLoader?: any; // FileLoader type depends on environment

  constructor() {
    this.parser = new DocumentParser({ autoDetectLanguage: true });
    this.currentLanguage = this.parser.getCurrentLanguage();
    this.elements = {};
    this.languageSelector = null;
    this.statusBar = null;
    this.uiManager = new UIManager();
    this.uiLanguageButton = null;
    this.authService = null; // Will be initialized if TYPO3 auth is enabled
    this.userManagementService = null; // Will be initialized after auth
    this.gatewayService = new GatewayService();
    this.accountSettings = null;
    this.discoveryPanel = null;
    this.contradictionOffcanvas = null;
    // Document history
    this.documentHistory = new DocumentHistory(
      10,
      STORAGE_KEYS.RECENT_DOCUMENTS
    );
    this.recentDocuments = null;
    // File handle manager for File System Access API
    this.fileHandleManager = new FileHandleManager();
    // Managers (initialized during initialize())
    this.documentManager = null;
    this.componentInitializer = null;

    // Toast notification service (singleton)
    this.toastService = ToastService.getInstance();
    this.splashScreen = new SplashScreen();
  }

  /**
   * Base initialization sequence - calls template methods that subclasses implement
   */
  async initialize(): Promise<void> {
    logger.debug('Initializing base app...');

    // Initialize UI Manager first (i18n) so the splash text is translated.
    await this.uiManager.initialize();

    // Gate boot on authentication BEFORE the expensive component init.
    // Unauthenticated users redirect here and never fall through.
    await this.runAuthGate();

    // The auth mode is settled: build the gateway every component will hold
    // (proxy with a TYPO3 session, else direct), before any component exists.
    this.gatewayService = this.gatewayInitializer().create();

    // Hook: prepare DOM before element lookup (web: premount modules, #92)
    await this.beforeSetupElements();

    // Get DOM elements (implemented by subclass)
    this.setupElements();

    // Initialize environment-specific components (implemented by subclass)
    await this.initializeEnvironmentSpecific();

    // Set up event handlers (implemented by subclass)
    this.setupEventHandlers();

    // Initialize component initializer and UI components
    this.componentInitializer = new ComponentInitializer(this as any);
    await this.componentInitializer.initializeAll();

    // Initialize document manager (must be after UI components)
    this.initializeDocumentManager();

    // Initialize Gateway service (reuses the gated authService)
    await this.initializeGatewayService();

    // Update UI
    this.updateUI();

    // Restore a document that was open before a forced auth redirect.
    this.restorePendingDocument();

    // Hook: last step before reveal (web: start the shell, #92)
    await this.beforeReveal();

    // Boot succeeded — reveal the app (no-op when no splash exists).
    this.splashScreen.hide();

    logger.info('Base app initialized successfully');
  }

  /**
   * Auth-first boot gate (web proxy mode only). Runs before the heavy
   * component init so unauthenticated users redirect to Ory without paying
   * for it. Sets `this.authService` on success; the gateway initializer then
   * reuses it instead of re-validating.
   *
   *  - not proxy mode        → returns, authService stays null (direct/dev).
   *  - authenticated         → stores the validated service, returns.
   *  - unauthenticated       → redirects; returns a never-resolving promise so
   *                            boot suspends and the app is never revealed.
   *  - ended                 → signed-out dialog (never redirects, never
   *                            resolves; the dialog owns the screen).
   *  - error                 → splash error + Retry (retry re-runs the gate);
   *                            with no splash, falls back to redirect.
   */
  protected async runAuthGate(): Promise<void> {
    const typo3BaseUrl = import.meta.env.VITE_TYPO3_BASE_URL;
    if (!typo3BaseUrl) {
      this.authService = null;
      return;
    }

    const authService = new Typo3AuthService({ typo3BaseUrl });
    this.listenForSessionEnd(authService);
    let status = await authService.getSessionStatus();

    // #113: a session that merely expired (idle, max age) while the app was
    // closed goes straight back through the login like a first visit; the
    // dialog stays for access that ended (removed member, suspended org).
    if (status === 'ended' && authService.getSessionEnd()?.kind === 'expired') {
      logger.info(
        'Session expired while the app was closed - signing in again'
      );
      status = 'unauthenticated';
    }

    if (status === 'authenticated') {
      logger.info('Session confirmed:', authService.getUser());
      clearLoginRedirects(); // the login round trip worked (#107)
      this.authService = authService;
      return;
    }

    if (status === 'unauthenticated') {
      if (isLoginLooping() && this.splashScreen.exists()) {
        // #107: the session never sticks (cookies blocked?) — explain
        // instead of bouncing app → login → app forever.
        logger.warn('Login keeps returning without a session - stopping');
        this.splashScreen.showError(() => {
          clearLoginRedirects();
          this.goToLogin(authService);
        }, this.uiManager.t('splash.loginLoop'));
        return new Promise<void>(() => {});
      }
      logger.warn('No valid session - redirecting to TYPO3 login');
      this.goToLogin(authService);
      return new Promise<void>(() => {}); // navigation happens; never resolve
    }

    if (status === 'ended') {
      // Not a first visit: explain (e.g. suspended organization) instead of
      // sending the user through login to the pending page.
      notifySessionEnded(authService.getSessionEnd()!);
      return new Promise<void>(() => {}); // the dialog owns the screen
    }

    // status === 'error'
    if (this.splashScreen.exists()) {
      logger.warn('Session check failed - showing splash error');
      await new Promise<void>((resolve) => {
        this.splashScreen.showError(() => resolve());
      });
      return this.runAuthGate(); // Retry re-runs the whole gate
    }

    logger.warn('Session check failed and no splash present - redirecting');
    this.goToLogin(authService);
    return new Promise<void>(() => {});
  }

  /**
   * Log out (#108): stop background work, remove what belongs to the person
   * (recent documents, per-tab auth leftovers) while keeping the device's
   * preferences, then let the backend end the BFF and Ory sessions.
   */
  protected logOut(authService: Typo3AuthService): void {
    try {
      this.gatewayService?.stopPolling?.();
      this.forgetPerson();
      broadcastAuthEvent({ type: 'logged-out' }); // the other tabs follow (#110)
    } catch (error) {
      logger.warn('Logout clean-up failed, logging out anyway:', error);
    } finally {
      // Always leave: a failing clean-up must not leave a dead Logout button.
      authService.redirectToLogout(this.uiManager.getCurrentLanguage?.());
    }
  }

  /**
   * Leave for the login: park the route (beforeAuthRedirect), count the
   * redirect for the loop guard and pass the UI language (#107).
   */
  protected goToLogin(authService: Typo3AuthService): void {
    this.beforeAuthRedirect();
    recordLoginRedirect();
    authService.redirectToLogin(this.uiManager.getCurrentLanguage?.());
  }

  /**
   * imsight#106: from now on a session loss (any request, any time) stops the
   * background polling and shows the signed-out dialog instead of redirecting.
   */
  protected listenForSessionEnd(authService: Typo3AuthService): void {
    this.listenToOtherTabs();
    setSessionEndedListener((info) => {
      logger.warn('Session ended:', info.reason);
      this.gatewayService?.stopPolling?.();
      if (info.kind === 'signed_out') {
        // Another tab logged out (#110): drop what this tab still holds, or its
        // next state change writes the history back to storage.
        this.forgetPerson();
      } else if (!info.fromOtherTab) {
        broadcastAuthEvent({
          type: 'session-ended',
          info: { kind: info.kind, reason: info.reason },
        });
      }
      // The boot splash (z 9999) would cover the dialog; hiding is idempotent.
      this.splashScreen?.hide();
      this.closeOpenOverlays();
      try {
        this.showSignedOutDialog(authService, info);
      } catch (error) {
        logger.error('Signed-out dialog failed, redirecting to login:', error);
        this.goToLogin(authService);
      }
    });
  }

  private crossTabWired = false;

  /**
   * #110: events from other tabs end this tab's session the same way a 401
   * would (first one wins, no echo); a hidden tab pauses health polling and a
   * visible one polls at once — unless the session has ended here.
   */
  private listenToOtherTabs(): void {
    if (this.crossTabWired) return; // runAuthGate's Retry calls us again
    this.crossTabWired = true;

    listenForAuthEvents((event) => {
      notifySessionEnded(
        event.type === 'logged-out'
          ? { kind: 'signed_out', reason: 'LOGGED_OUT', fromOtherTab: true }
          : { ...event.info, fromOtherTab: true }
      );
    });

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        this.gatewayService?.pausePolling?.();
      } else if (!hasSessionEnded()) {
        this.gatewayService?.resumePolling?.({ immediate: true });
      }
    });
  }

  /** Person data in storage and in memory (#108, #110); device preferences stay. */
  private forgetPerson(): void {
    clearPersonData();
    useUserStore.getState().reset();
  }

  /** Bootstrap stacks an open modal above a second backdrop; close them first. */
  private closeOpenOverlays(): void {
    document.querySelectorAll('.modal.show').forEach((el) => {
      Modal.getInstance(el)?.hide();
    });
    document.querySelectorAll('.offcanvas.show').forEach((el) => {
      Offcanvas.getInstance(el)?.hide();
    });
  }

  protected showSignedOutDialog(
    authService: Typo3AuthService,
    info: SessionEndInfo
  ): void {
    new SignedOutDialog({
      t: (key, params) => this.uiManager.t(key, params),
      kind: info.kind,
      organizationName: authService.getOrganizationName(),
      onSignIn: () => {
        // A deliberate click: counted for the loop guard, never blocked. After a
        // logout elsewhere nothing of the person is parked (#110).
        if (info.kind !== 'signed_out') {
          saveDocumentForRestore(useAppStore.getState().currentDocument);
        }
        this.goToLogin(authService);
      },
      onSignOut: () => this.logOut(authService),
    }).show();
  }

  /**
   * Boot hooks (#92). No-ops here; module-agnostic by design — the web
   * shell uses them, the Word add-in does not.
   */

  /** Called immediately before any redirect to the login page. */
  protected beforeAuthRedirect(): void {}

  /** Called after the auth gate passes and before setupElements(). */
  protected async beforeSetupElements(): Promise<void> {}

  /** Called after boot completes and before the splash hides. */
  protected async beforeReveal(): Promise<void> {}

  /**
   * If document metadata was saved before a forced login redirect, inform
   * the user that they need to reload their document. We no longer store
   * the full document content in sessionStorage for security reasons.
   */
  private restorePendingDocument(): void {
    const saved: DocumentRestoreMetadata | null = loadSavedDocument();
    if (!saved) return;
    logger.info('Document metadata found after auth redirect:', saved.name);
    this.toastService.showInfo(
      this.uiManager.t('auth.documentNotRestored', { name: saved.name })
    );
  }

  /**
   * Abstract methods that subclasses must implement
   */
  setupElements(): void {
    throw new Error('setupElements must be implemented by subclass');
  }

  setupEventHandlers(): void {
    throw new Error('setupEventHandlers must be implemented by subclass');
  }

  async initializeEnvironmentSpecific(): Promise<void> {
    // Optional - subclasses can override if needed
  }

  loadDocument(): void {
    throw new Error('loadDocument must be implemented by subclass');
  }

  displayDocument(_document: Document): void {
    throw new Error('displayDocument must be implemented by subclass');
  }

  /**
   * Initialize UI language button with Word-specific fallback handling
   * Delegates to ComponentInitializer
   */
  initializeUILanguageButton(): void {
    if (this.componentInitializer) {
      this.componentInitializer.initializeUILanguageButton();
    }
  }

  /**
   * Initialize document language selector
   * Delegates to ComponentInitializer
   */
  initializeLanguageSelector(): void {
    if (this.componentInitializer) {
      this.componentInitializer.initializeLanguageSelector();
    }
  }

  /**
   * Initialize recent documents component
   * Delegates to ComponentInitializer
   */
  initializeRecentDocuments(): void {
    if (this.componentInitializer) {
      this.componentInitializer.initializeRecentDocuments();
    }
  }

  /**
   * Handle click on a document in the document list
   * Opens the document URL if available
   */
  handleDocumentListClick(doc: any): void {
    logger.debug('Document clicked:', doc);

    if (doc.url) {
      window.open(doc.url, '_blank', 'noopener,noreferrer');
    } else {
      this.toastService.showInfo('No URL available for this document');
    }
  }

  /**
   * Initialize document manager
   */
  initializeDocumentManager(): void {
    this.documentManager = new DocumentManager({
      documentHistory: this.documentHistory,
      fileHandleManager: this.fileHandleManager,
      statusBar: this.statusBar!,
      languageSelector: this.languageSelector!,
      recentDocuments: this.recentDocuments!,
      uiManager: this.uiManager,
      parser: this.parser,
      displayDocument: this.displayDocument.bind(this),
      updateEnhancementMenu: this.updateEnhancementMenu.bind(this),
      setStatus: (this as any).setStatus
        ? (this as any).setStatus.bind(this)
        : null,
      setLoading: (this as any).setLoading
        ? (this as any).setLoading.bind(this)
        : null,
      buildDocumentInfoHTML: (this as any).buildDocumentInfoHTML
        ? (this as any).buildDocumentInfoHTML.bind(this)
        : null,
    });

    logger.debug('Document manager initialized');
  }

  /**
   * Initialize status bar component
   * Delegates to ComponentInitializer
   */
  initializeStatusBar(): void {
    if (this.componentInitializer) {
      this.componentInitializer.initializeStatusBar();
    }
  }

  /**
   * Hook for subclasses to retrieve the API key from environment-specific sources
   * @returns API key or null if not found
   */
  getApiKey(): string | null {
    // Default implementation: check Vite env
    return import.meta.env.VITE_API_KEY || null;
  }

  /**
   * Hook for subclasses to handle no API key scenario
   * Called when API key is not found
   */
  handleNoApiKey(): void {
    logger.warn('No API_KEY found - Gateway service will not be available');

    // Gateway service is not initialized without API key, so no health state to report
  }

  private gatewayInitializer(): GatewayInitializer {
    return new GatewayInitializer({
      authService: this.authService, // pre-validated by runAuthGate()
      getApiKey: () => this.getApiKey(),
      handleNoApiKey: () => this.handleNoApiKey(),
    });
  }

  /**
   * Initialize Gateway service (Template Method)
   * Subclasses can override hooks: getApiKey(), handleNoApiKey()
   */
  async initializeGatewayService(): Promise<void> {
    const initialized = this.gatewayInitializer().start(this.gatewayService);

    // Initialize user management service if auth is available
    if (this.authService) {
      this.initializeUserManagementService();
    }

    if (initialized) {
      this.discoveryPanel?.onGatewayReady();
      try {
        const labels = await this.gatewayService.getInteractionLabels();
        interactionLabels.setLabels(labels);
        logger.debug('Interaction labels loaded from gateway');
      } catch (error) {
        logger.warn(
          'Failed to load interaction labels; using bundled defaults',
          error
        );
      }
    }
  }

  /**
   * Initialize user management service and load user data
   */
  protected initializeUserManagementService(): void {
    if (!this.authService) {
      logger.warn('Auth service not available - skipping user management init');
      return;
    }

    // Create user management service
    this.userManagementService = new UserManagementService({
      typo3BaseUrl:
        (this.authService as any).typo3BaseUrl || 'https://www.gitterdan.ai',
      authService: this.authService,
    });

    // Update component initializer with services
    if (this.componentInitializer) {
      this.componentInitializer.updateAccountSettingsUserService(
        this.userManagementService
      );
    }

    // Load user data into store
    this.loadUserData();

    logger.debug('User management service initialized');
  }

  /**
   * Load user data from auth service into store
   */
  protected loadUserData(): void {
    if (!this.authService) return;

    const user = this.authService.getUser();
    if (user) {
      useUserStore.getState().setUser({
        id: user.id,
        email: user.email,
        firstName: user.firstName,
        lastName: user.lastName,
      });
      logger.debug('User data loaded into store');
    }
  }

  /**
   * Get the appropriate language code for Gateway API calls
   * Uses ISO 639-3 format as required by the Gateway
   */
  getGatewayLanguageCode(): string {
    const langCode = this.currentDocument
      ? this.getDocumentLanguageCode()
      : this.uiManager.getCurrentLanguage();

    return toIso6393(langCode);
  }

  /**
   * Get the language code for the current document
   */
  private getDocumentLanguageCode(): LanguageCode {
    if (this.languageSelector && this.languageSelector.isAutoDetectEnabled()) {
      return this.currentDocument!.parsed.metadata.language;
    }
    return this.languageSelector
      ? (this.languageSelector.getCurrentLanguage() as LanguageCode)
      : this.currentLanguage;
  }

  /**
   * Handle language selection change (document language)
   */
  handleLanguageChange(
    languageCode: LanguageCode,
    autoDetected?: boolean
  ): void {
    logger.info(`Language changed to: ${languageCode} (auto: ${autoDetected})`);
    this.currentLanguage = languageCode;
    this.parser.setLanguage(languageCode);

    // Language selector in status bar now handles its own display
    // No need to update status bar separately

    // Re-parse current document if available
    if (this.currentDocument) {
      this.reprocessDocument();
    }
  }

  /**
   * Handle auto-detect toggle
   */
  handleAutoDetectToggle(enabled: boolean): void {
    logger.debug(`Auto-detect ${enabled ? 'enabled' : 'disabled'}`);
    this.parser.setAutoDetectLanguage(enabled);

    // Re-process document if auto-detect is enabled and document exists
    if (enabled && this.currentDocument) {
      this.reprocessDocument();
    }
  }

  /**
   * Re-process current document with current language settings (delegates to DocumentManager)
   */
  async reprocessDocument(): Promise<void> {
    await this.documentManager!.reprocessDocument();

    // Update current language from document manager
    this.currentLanguage = this.documentManager!.getCurrentLanguage();
  }

  /**
   * Handle successful document loading (delegates to DocumentManager)
   */
  handleDocumentLoaded(
    document: Document,
    fileHandle: FileSystemFileHandle | null = null
  ): void {
    this.documentManager!.handleDocumentLoaded(document, fileHandle);

    // Update current language from document manager
    this.currentLanguage = this.documentManager!.getCurrentLanguage();
  }

  /**
   * Handle document selected from recent documents list (delegates to DocumentManager)
   * @param docMetadata - Document metadata from history
   */
  async handleRecentDocumentSelect(docMetadata: any): Promise<void> {
    return this.documentManager!.handleRecentDocumentSelect(
      docMetadata,
      this.fileLoader
    );
  }

  /**
   * Update document information display
   * Delegates to DocumentManager
   */
  updateDocumentInfo(document: Document): void {
    this.documentManager!.updateDocumentInfo(
      document,
      this.elements.documentInfo || null
    );
  }

  /**
   * Update the analysis buttons after the document changed (hook; the web
   * app enables its Analyse buttons here)
   */
  updateEnhancementMenu(): void {}

  /**
   * Show error message using toast notification
   */
  showError(message: string): void {
    logger.error('Error:', message);
    this.toastService.showError(message);
  }

  /**
   * Show success message using toast notification
   */
  showSuccess(message: string): void {
    this.toastService.showSuccess(message);
  }

  /**
   * Show info message using toast notification
   */
  showInfo(message: string): void {
    this.toastService.showInfo(message);
  }

  /**
   * Show warning message using toast notification
   */
  showWarning(message: string): void {
    logger.warn('Warning:', message);
    this.toastService.showWarning(message);
  }

  /**
   * Update UI based on current state (template method for subclasses)
   */
  updateUI(): void {
    // Base implementation - subclasses can override
  }

  /**
   * Get current document
   */
  getCurrentDocument(): Document | null {
    return this.currentDocument;
  }

  /**
   * Get the full text content from the current document
   * Delegates to DocumentManager
   * @returns Document text
   */
  getDocumentText(): string {
    return this.documentManager ? this.documentManager.getDocumentText() : '';
  }

  /**
   * Run stateless rule checking on the current document via CHECK_RULES.
   * Called by the web app's "Process" button.
   */
  async checkDocumentRules(): Promise<void> {
    if (!this.currentDocument) {
      this.toastService.showWarning('No document loaded.');
      return;
    }

    const text = this.getDocumentText();
    if (!text || text.trim().length === 0) {
      this.toastService.showWarning('Document has no text content.');
      return;
    }

    const languageCode = this.getGatewayLanguageCode();
    const docName = this.currentDocument?.name;

    this.toastService.showInfo('Analysing document…');

    try {
      const result = await this.gatewayService.checkRules(text, languageCode, {
        domain: useAppStore.getState().relationsDomain || 'construction',
        source_document: docName,
        onProgress: (done, total) => {
          if (total > 1) {
            this.toastService.showInfo(`Processing section ${done}/${total}…`);
          }
        },
      });

      this.applyCheckResponse(result);

      if (result.chunked) {
        const failedMsg = result.failed_chunks
          ? ` ${result.failed_chunks} section(s) could not be processed.`
          : '';
        this.toastService.showWarning(
          `Large document analyzed in parts; contradictions spanning part boundaries may not be detected.${failedMsg}`
        );
      }

      if (result.check_error) {
        this.toastService.showWarning(
          `Rule check partially failed: ${result.check_error}`
        );
      }

      logger.info(
        `Rules checked: ${result.statement_count} statements, ` +
          `${result.conflicts?.length ?? 0} conflicts`
      );

      this.toastService.showSuccess(
        `Found ${result.statement_count} statements, ` +
          `${result.conflicts?.length ?? 0} conflicts`
      );
    } catch (error) {
      useAppStore.getState().setLastCheckResponse(null);
      useAppStore.getState().setStatementNumberMap(new Map());
      clearRuleHighlights();
      this.detachRuleClickListener?.();
      this.detachRuleClickListener = null;
      logger.error('Failed to check rules:', error);
      // The gateway workspace expired (409), not the user's login: say so plainly.
      this.toastService.showError(
        isGatewayError(error, GatewayErrorCode.SESSION_EXPIRED)
          ? this.uiManager.t('errors.analysisSessionExpired')
          : (forbiddenMessage(error, (k) => this.uiManager.t(k)) ??
              `Rule check failed: ${(error as Error).message}`)
      );
    }
  }

  /**
   * Show a CheckRulesResponse on the loaded document: statement numbers,
   * highlights, the rules panel (via the store) and the click wiring. Shared by
   * a live check and a stored analysis (#98 part 4), which makes no gateway call.
   */
  applyCheckResponse(result: CheckRulesResponse): void {
    const statementNumbers = highlightAllStatements(result.statements);
    useAppStore.getState().setStatementNumberMap(statementNumbers);
    useAppStore.getState().setLastCheckResponse(result);
    // #96: the Todo placeholder lists processed documents with conflicts.
    const current = this.currentDocument;
    if (current) {
      recordProcessedDocument(current, result.conflicts?.length ?? 0);
    }

    this.wireDocumentRuleClicks();
  }

  /**
   * Reopen the rules offcanvas with the most recent rule-check results.
   * Called by the web app's "Rules" button.
   */
  showContradictionsOffcanvas(): void {
    this.contradictionOffcanvas?.show();
  }

  /**
   * Attach the document → offcanvas click listener (#72). Replaces any
   * previously attached listener so repeated CHECK_RULES cycles do not
   * accumulate handlers.
   */
  private wireDocumentRuleClicks(): void {
    this.detachRuleClickListener?.();
    this.detachRuleClickListener = null;

    const container = document.getElementById('document-content');
    if (!container) return;

    this.detachRuleClickListener = attachRuleClickListener(container, (iri) => {
      this.contradictionOffcanvas?.show();
      this.contradictionOffcanvas?.scrollToRule(iri);
    });
  }

  /**
   * Get current document (backward compatibility)
   * @returns Current document
   */
  get currentDocument(): Document | null {
    return this.documentManager
      ? this.documentManager.getCurrentDocument()
      : null;
  }

  /**
   * Set current document (tests assign it)
   * @param document - Document to set
   */
  set currentDocument(document: Document | null) {
    if (this.documentManager) {
      (this.documentManager as any).currentDocument = document;
    }
  }
}

export default BaseApp;
